import { useCallback, useEffect, useState } from 'react';
import { Box, makeStyles, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import {
  DataGrid,
  GridColumnProps,
  LinkColumn,
  StandardDialog,
  VerticalBarLoader,
} from '@roc/ui';
import { filterProps, noOpComparator } from '@roc/ui/utils';
import { UserMediaGalleryReport } from './userMediaGalleryReport';
import { GalleryUser } from '../types/galleryTypes';

const columns: GridColumnProps[] = [
  {
    field: 'displayName',
    headerName: 'User Name',
    cellRenderer: 'userNameCellRenderer',
    minWidth: 260,
    ...filterProps(),
  },
  {
    field: 'userName',
    headerName: 'Username',
    minWidth: 200,
    ...filterProps(),
  },
  {
    field: 'userId',
    headerName: 'User ID',
    minWidth: 120,
    comparator: noOpComparator,
    sortable: false,
  },
  {
    field: 'folderCount',
    headerName: 'Folders',
    minWidth: 120,
    comparator: noOpComparator,
    sortable: false,
  },
];

const useStyles = makeStyles(() => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: 'calc(85vh - 160px)',
    overflow: 'hidden',
  },
  dialog: {
    '& .MuiDialogContent-root': {
      padding: 0,
      '& > .MuiBox-root': {
        margin: 0,
      },
    },
  },
  smallScreen: {
    '& .MuiDialog-paper': {
      margin: '4px',
    },
    '& .MuiDialog-paperFullWidth': {
      width: 'calc(100% - 8px)',
    },
    '& .MuiDialog-paperScrollPaper': {
      maxHeight: 'calc(100% - 8px)',
    },
  },
}));

export const UsersMediaGallery = observer(() => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const { galleryStore } = inspectionStore;
  const { usersGridStore } = galleryStore;

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [, setGridApi] = useState(null);

  useEffect(() => {
    usersGridStore.resetAndFetchGridData();
    return () => galleryStore.reset();
  }, []);

  const handleOpenUser = (user: GalleryUser) => {
    galleryStore.openUser(user);
  };

  const handleCloseReport = () => {
    galleryStore.closeUser();
  };

  const onGridReady = useCallback(params => {
    setGridApi(params.api);
  }, []);

  const onSortChanged = useCallback(
    params => {
      usersGridStore.setSortModel(params.api.getSortModel());
    },
    [usersGridStore]
  );

  const onFilterChanged = useCallback(
    params => {
      usersGridStore.setFilterModel(params.api.getFilterModel());
    },
    [usersGridStore]
  );

  const frameworkComponents = {
    userNameCellRenderer: ({ value, data }: { value: any; data: any }) => (
      <LinkColumn
        value={value}
        url={'#'}
        textUnderline
        onClick={(event: any) => {
          event.preventDefault();
          handleOpenUser(data);
        }}
      />
    ),
  };

  return (
    <>
      <Box className={classes.gridContainer}>
        <DataGrid
          columns={columns}
          rows={usersGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          isLoading={usersGridStore.isLoading}
          showFilters={usersGridStore.showFilters}
          sortModel={usersGridStore.sortModel}
          filterModel={usersGridStore.filterModel}
          paginationData={usersGridStore.gridData.meta}
          setPageNumber={usersGridStore.setPageNumber}
          gridReady={onGridReady}
        />
      </Box>

      <StandardDialog
        className={clsx(classes.dialog, smallScreen && classes.smallScreen)}
        open={!!galleryStore.selectedUser}
        handleClose={handleCloseReport}
        maxWidth="xl"
        removeContentBox
        dialogContent={galleryStore.selectedUser && <UserMediaGalleryReport />}
      />

      {galleryStore.isLoading && <VerticalBarLoader />}
    </>
  );
});
